import { useCallback, useEffect, useRef } from "react";
import type { RefObject } from "react";
import throttle from "lodash/throttle";
import { v4 as uuidv4 } from "uuid";
import type { PropertiesType } from "@/react/api/analyticsTrackerApi";
import { analyticsTrackerApi } from "@/react/api/analyticsTrackerApi";
import { usePunditUserContext } from "@/react/contexts";
import { usePageViewPropertiesContext } from "./pageViewPropertiesContext";

const MINIMUM_WATCH_THRESHOLD = 30;
const MINIMUM_DURATION_FOR_SEGMENT_TRACKING = 30;
const SEGMENTS_TO_TRACK = [25, 50, 75, 90];

interface VideoViewProperties extends PropertiesType {
  community_id?: number;
  community_member_id?: number;
  page_view_id: string;
  signed_id?: string;
  video_duration: number;
  video_url: string;
  video_view_id: string;
}

interface VideoSegmentViewProperties extends VideoViewProperties {
  progress_percent: number;
  progress_seconds: number;
}

export const useVideoAnalytics = (
  videoRef: RefObject<HTMLVideoElement>,
  signedId?: string,
  enabled: boolean = true,
) => {
  const viewIdRef = useRef(uuidv4());
  const hasTrackedRef = useRef(false);
  const setOfSegmentAlreadyTracked = useRef<Set<number>>(new Set());
  const setOfSegmentStartedPlayingRef = useRef<Set<number>>(new Set());

  const { pageViewId } = usePageViewPropertiesContext();
  const {
    masqueradingEntity,
    currentCommunity,
    currentCommunityMember,
    isLoading,
  } = usePunditUserContext();

  const communityId = currentCommunity?.id;
  const communityMemberId = currentCommunityMember?.id;
  const shouldSkipAnalytics = !!(
    masqueradingEntity ||
    isLoading ||
    !signedId ||
    !enabled
  );

  // Reset tracking state and generate new view ID when the video signedId changes.
  useEffect(() => {
    if (!signedId) return;
    hasTrackedRef.current = false;
    viewIdRef.current = uuidv4();
    setOfSegmentAlreadyTracked.current = new Set();
    setOfSegmentStartedPlayingRef.current = new Set();
  }, [signedId]);

  const trackVideoView = useCallback(async () => {
    if (shouldSkipAnalytics || !videoRef.current || hasTrackedRef.current)
      return;

    const video = videoRef.current;

    const properties: VideoViewProperties = {
      community_id: communityId,
      community_member_id: communityMemberId,
      video_url: video.currentSrc,
      video_duration: Math.round(video.duration),
      video_view_id: viewIdRef.current,
      page_view_id: pageViewId ?? "unknown",
      signed_id: signedId,
    };

    await analyticsTrackerApi.trackVideoView(properties);
    hasTrackedRef.current = true;
  }, [
    communityId,
    communityMemberId,
    shouldSkipAnalytics,
    videoRef,
    signedId,
    pageViewId,
  ]);

  const trackVideoSegmentView = useCallback(
    async (progressPercent: number, progressSeconds: number) => {
      if (shouldSkipAnalytics || !videoRef.current) return;

      const video = videoRef.current;

      const properties: VideoSegmentViewProperties = {
        community_id: communityId,
        community_member_id: communityMemberId,
        video_url: video.currentSrc,
        video_duration: Math.round(video.duration),
        video_view_id: viewIdRef.current,
        page_view_id: pageViewId ?? "unknown",
        signed_id: signedId,
        progress_percent: progressPercent,
        progress_seconds: progressSeconds,
      };

      await analyticsTrackerApi.trackVideoSegmentView(properties);
    },
    [
      communityId,
      communityMemberId,
      shouldSkipAnalytics,
      videoRef,
      signedId,
      pageViewId,
    ],
  );

  useEffect(() => {
    if (!videoRef.current) return;
    if (!signedId) return;

    const video = videoRef.current;

    const throttledOnTimeUpdate = throttle(() => {
      if (hasTrackedRef.current) return;

      const videoDuration = video.duration;
      const currentTime = video.currentTime;

      const isShortVideoStarted =
        videoDuration <= MINIMUM_WATCH_THRESHOLD && currentTime > 0;
      const isLongVideoThresholdMet =
        videoDuration > MINIMUM_WATCH_THRESHOLD &&
        currentTime >= MINIMUM_WATCH_THRESHOLD;

      if (isShortVideoStarted || isLongVideoThresholdMet) {
        void trackVideoView();
      }
    }, 250);

    video.addEventListener("timeupdate", throttledOnTimeUpdate);

    return () => {
      throttledOnTimeUpdate.cancel();
      video.removeEventListener("timeupdate", throttledOnTimeUpdate);
    };
  }, [trackVideoView, videoRef, signedId, pageViewId]);

  // Segment tracking
  useEffect(() => {
    if (!videoRef.current) return;
    if (!signedId) return;
    const video = videoRef.current;

    const handleProgress = throttle(() => {
      if (!video.duration) return;
      // Tracking segments for such small videos is not useful
      if (video.duration < MINIMUM_DURATION_FOR_SEGMENT_TRACKING) return;
      // Current limitation
      // - Does not play well with video seeking

      const currentPlayPercent = (video.currentTime / video.duration) * 100;
      const currentSegment = SEGMENTS_TO_TRACK.find(
        segment => currentPlayPercent < segment,
      );

      if (currentSegment) {
        // Help handle cases when a segment was never played because:
        // 1. User skipped the segment via progress bar seek
        // 2. When play started from a different segment on video reload/revisit. This is when new video playback starts from
        // previous location.
        setOfSegmentStartedPlayingRef.current.add(currentSegment);
      }

      const segmentToTrack = SEGMENTS_TO_TRACK.find((segment, index) => {
        const nextSegment = SEGMENTS_TO_TRACK[index + 1] || 100;
        return (
          currentPlayPercent >= segment && currentPlayPercent < nextSegment
        );
      });

      const shouldTrackSegment =
        segmentToTrack &&
        !setOfSegmentAlreadyTracked.current.has(segmentToTrack) &&
        setOfSegmentStartedPlayingRef.current.has(segmentToTrack);

      if (shouldTrackSegment) {
        setOfSegmentAlreadyTracked.current.add(segmentToTrack);
        void trackVideoSegmentView(
          segmentToTrack,
          Math.round(video.currentTime),
        );
      }
    }, 250);

    video.addEventListener("timeupdate", handleProgress);

    return () => {
      handleProgress.cancel();
      video.removeEventListener("timeupdate", handleProgress);
    };
  }, [trackVideoSegmentView, videoRef, signedId]);
};
